import type { AxiosRequestConfig, AxiosError } from "axios";
import qs from "query-string";
import type { BaseQueryFn, FetchArgs } from "@reduxjs/toolkit/query";
import { getCookie, hasWindow, setCookie } from "@litres/utils";
import axios from "../../instance/axios";
import type { RootState } from "../../store";
import { getGlobalHeaders } from "../../toolkit/browser/browserSlice";
import { getAPITimeout } from "../../api-timeout";

/*
* переопределяем header, потому что там он уже определен а FetchArgs и имеет следующую сигнатуру
* headers?:
      | Headers
      | string[][]
      | Record<string, string | undefined>
      | undefined
* эта сигнатура нам не подходит, т.к. мы используем Record<string, string | number>
* */
export interface AxiosBaseQueryArgs extends Omit<FetchArgs, "headers"> {
  url: string;
  method?: AxiosRequestConfig["method"];
  body?: AxiosRequestConfig["data"];
  params?: AxiosRequestConfig["params"];
  headers?: Record<string, string | number | boolean | undefined>;
}

export interface AxiosBaseQueryError {
  code?: string;
  status?: number;
  data: unknown;
  meta: {
    headers: Record<string, unknown>;
    url?: string;
    requestUrl: string;
  };
}

type AxiosBaseQuery = (params: {
  baseUrl: string;
}) => BaseQueryFn<AxiosBaseQueryArgs, unknown, AxiosBaseQueryError>;

export const axiosBaseQuery: AxiosBaseQuery =
  ({ baseUrl }) =>
  async (
    { url, method = "get", body, params, headers },
    { getState, endpoint }
  ) => {
    try {
      let requestHeaders: Record<
        string,
        string | number | boolean | undefined
      > = {};

      /* запрос оглавления в карточке книги не работает с кастомными хэдерами
       * также наши внутренние заголовки не нужны для запроса аб тестов у вариокуба
       * */
      if (endpoint !== "getArtTableContent" && endpoint !== "getVarioqubData") {
        const globalState = getState() as RootState;
        const globalHeaders = getGlobalHeaders(globalState);
        let SID;

        if (hasWindow) {
          SID = getCookie("SID") || "";
        } else {
          SID = globalState.foundationUser.SID;
        }

        requestHeaders["app-id"] = "115";

        if (SID) {
          requestHeaders["Session-Id"] = SID;
        }

        requestHeaders["Ui-Language-Code"] = globalState.face.language || "ru";

        requestHeaders = { ...globalHeaders, ...requestHeaders, ...headers };
      }

      /*
       * для запроса оглавления в карточке книги используется xml, который отдается только с www.litres.ru
       * поэтому при вызове хука RTKQ передается полный урл url
       * запрос для получения данных об аб-тестах идет на https://uaas.yandex.ru/v1/exps
       */
      const customUrl =
        endpoint === "getArtTableContent" || endpoint === "getVarioqubData"
          ? url
          : baseUrl + url;

      /*
       * для запроса оглавления и varioqub не передаем сredentials, чтобы не иметь проблем CORS
       */
      const withCredentials =
        endpoint !== "getArtTableContent" && endpoint !== "getVarioqubData";

      const result = await axios({
        url: customUrl,
        headers: requestHeaders,
        data: body,
        method,
        params,
        withCredentials,
        paramsSerializer: params =>
          qs.stringify(params, { arrayFormat: "none" }),
        timeout: getAPITimeout(
          url.includes("foundation") ? "foundation" : "advertising",
          hasWindow,
          customUrl
        ),
      });

      // всегда при запросах фаундейшн если приходит заголовок Request-Session-Id  - ставить его как куку SID
      // https://mybook.slack.com/archives/C03M9CAGE0K/p1681285116505019
      const requestSessionId = result.headers["request-session-id"] || "";
      const isFoundationRequest = url.indexOf("foundation") >= 0;
      if (isFoundationRequest && !!requestSessionId) {
        const twoMonth = 1000 * 60 * 60 * 24 * 60;
        setCookie("SID", requestSessionId, { "max-age": twoMonth });
      }
      return {
        data: result.data,
        meta: {
          headers: { ...result.headers },
        },
      };
    } catch (axiosError) {
      const err = axiosError as AxiosError;

      const error: AxiosBaseQueryError = {
        code: err.code,
        status: err.response?.status,
        data: err.response?.data || err.message,
        meta: {
          headers: err.response?.headers ?? {},
          // Неизвестно, что за поле. Сейчас всегда undefined
          url: err.response?.config.url,
          requestUrl: url,
        },
      };
      return { error };
    }
  };
